/* Side-effect effects for CallContext: SSE routing, push hydration,
 * SW→React bridge, visibility safety net, hang-up polling, remote-peer
 * left listener. Extracted to keep CallContext.jsx under 200 LOC. */
import { useEffect, useRef } from "react";
import { getCall as apiGetCall, listRecentCalls as apiListRecentCalls, rejectCall as apiRejectCall } from "../../lib/callsApi";
import { unlockAudio } from "../../lib/ringer";
import { prewarmAgora } from "../../hooks/useVoiceCall";
import { PHASE } from "./_phase";

export function usePrewarmAgora(userId) {
  useEffect(() => {
    if (!userId) return;
    const run = () => prewarmAgora();
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = window.requestIdleCallback(run, { timeout: 2500 });
      return () => { try { window.cancelIdleCallback(id); } catch { /* noop */ } };
    }
    const t = setTimeout(run, 1500);
    return () => clearTimeout(t);
  }, [userId]);
}

// Route SSE messaging-stream events into CallContext state.
export function routeMessagingEvent(ev, ctx) {
  const { userId, activeCallRef, setActiveCall, setPeer, setPhase, setCallStartedAt, reset } = ctx;
  if (!ev || !userId) return;
  if (ev.type === "call:incoming" && ev.call) {
    if (activeCallRef.current) return;
    setActiveCall(ev.call);
    setPeer(ev.call.caller || null);
    setPhase(PHASE.INCOMING);
    return;
  }
  if (ev.type === "call:outgoing" && ev.call) return;
  const current = activeCallRef.current;
  if (!current || ev.call_id !== current.id) return;
  if (ev.type === "call:answered") {
    setPhase(PHASE.IN_CALL);
    setCallStartedAt(Date.now());
  } else if (["call:rejected", "call:canceled", "call:ended", "call:missed"].includes(ev.type)) {
    reset();
  }
}

// Hydrate call state from ?call=<id> URL param (push-notification landing).
export function useUrlCallHydration({ user, location, activeCallRef, setActiveCall, setPeer, setPhase }) {
  const hydratedCallIdRef = useRef(null);
  useEffect(() => {
    if (!user?.id) return;
    const params = new URLSearchParams(location.search);
    const callId = params.get("call");
    const action = params.get("call_action");
    if (!callId || hydratedCallIdRef.current === callId) return;
    hydratedCallIdRef.current = callId;

    const cleaned = new URLSearchParams(location.search);
    cleaned.delete("call"); cleaned.delete("call_action");
    const next = `${location.pathname}${cleaned.toString() ? `?${cleaned}` : ""}${location.hash}`;
    try { window.history.replaceState(null, "", next); } catch { /* noop */ }

    (async () => {
      try {
        const call = await apiGetCall(callId);
        if (!call || !["ringing", "in_progress"].includes(call.status)) return;
        if (action === "decline" && call.callee_id === user.id && call.status === "ringing") {
          try { await apiRejectCall(callId); } catch { /* noop */ }
          return;
        }
        if (call.callee_id !== user.id || call.status !== "ringing") return;
        if (activeCallRef.current) return;
        unlockAudio();
        setActiveCall({
          id: call.id,
          conversation_id: call.conversation_id,
          channel: call.channel,
          caller: call.caller || null,
          created_at: call.created_at,
        });
        setPeer(call.caller || null);
        setPhase(PHASE.INCOMING);
      } catch { /* silent */ }
    })();
  }, [user?.id, location.search, location.pathname, location.hash]);
}

// SW→React bridge — soft-navigate on notification tap.
export function useSwNavBridge(navigate) {
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return undefined;
    const onMessage = (event) => {
      const msg = event.data;
      if (!msg || msg.type !== "lexiq:notif-click") return;
      const target = msg.link || "/notifications";
      try { navigate(target); } catch { /* noop */ }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);
}

// Visibility-restore safety net: catch missed call:incoming SSEs.
export function useRingingSafetyNet({ user, activeCallRef, setActiveCall, setPeer, setPhase }) {
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    const check = async () => {
      if (cancelled || document.visibilityState !== "visible" || activeCallRef.current) return;
      try {
        const data = await apiListRecentCalls(5);
        const ringing = (data?.items || []).find(
          (c) => c.direction === "incoming" && c.status === "ringing",
        );
        if (!ringing || activeCallRef.current) return;
        setActiveCall({
          id: ringing.id,
          conversation_id: ringing.conversation_id,
          channel: null,
          caller: ringing.peer,
          created_at: ringing.created_at,
        });
        setPeer(ringing.peer || null);
        setPhase(PHASE.INCOMING);
      } catch { /* silent */ }
    };
    const onVis = () => { check(); };
    document.addEventListener("visibilitychange", onVis);
    check();
    return () => { cancelled = true; document.removeEventListener("visibilitychange", onVis); };
  }, [user?.id]);
}

// Active-call hang-up polling — 4s tick, tears down on terminal status.
export function useHangupPoll({ activeCallId, phase, reset }) {
  useEffect(() => {
    if (!activeCallId) return undefined;
    if (![PHASE.INCOMING, PHASE.DIALING, PHASE.IN_CALL].includes(phase)) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const call = await apiGetCall(activeCallId);
        if (!call) return;
        if (!["ringing", "in_progress"].includes(call.status)) {
          if (!cancelled) reset();
        }
      } catch (e) {
        if (e?.response?.status === 404 && !cancelled) reset();
      }
    };
    const interval = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [activeCallId, phase, reset]);
}

// Agora remote-peer left listener — sub-second teardown when the peer's
// device drops the RTC connection.
export function useRemotePeerLeft(phase, reset) {
  useEffect(() => {
    if (phase !== PHASE.IN_CALL) return undefined;
    const onLeft = () => { reset(); };
    window.addEventListener("lex:remote-peer-left", onLeft);
    return () => window.removeEventListener("lex:remote-peer-left", onLeft);
  }, [phase, reset]);
}
